import * as RailroadImport from 'railroad-diagrams';
import { mount } from '@crown/framework/dom';
import type { Instruction } from '@crown/framework/ir';
import errorTemplate from './railroad-error.tsrx';

export interface RailroadErrorProps {
  /** Why the diagram could not be drawn. */
  readonly message: string;
}

const RailroadError: (props: RailroadErrorProps) => Instruction = errorTemplate;

type RailroadLeafSpec =
  | string
  | { type: 'Terminal'; text: string }
  | { type: 'NonTerminal'; text: string }
  | { type: 'Comment'; text: string }
  | { type: 'Skip' };

type RailroadContainerSpec =
  | { type: 'Sequence'; items: RailroadSpec[] }
  | { type: 'Choice'; normal?: number; items: RailroadSpec[] }
  | { type: 'Optional'; item: RailroadSpec; skip?: 'skip' }
  | { type: 'OneOrMore'; item: RailroadSpec; repeat?: RailroadSpec }
  | {
      type: 'ZeroOrMore';
      item: RailroadSpec;
      repeat?: RailroadSpec;
      skip?: 'skip';
    }
  // Both are rendered below and declared on the library; leaving them out of
  // the union narrowed those branches to `never`.
  | { type: 'Diagram'; items: RailroadSpec[] }
  | { type: 'ComplexDiagram'; items: RailroadSpec[] }
  | { type: 'Diagram'; items: RailroadSpec[] }
  | { type: 'ComplexDiagram'; items: RailroadSpec[] };

type RailroadSpec = RailroadLeafSpec | RailroadContainerSpec | RailroadSpec[];

type RailroadLibrary = {
  Diagram: (...items: unknown[]) => RailroadRenderable;
  ComplexDiagram: (...items: unknown[]) => RailroadRenderable;
  Sequence: (...items: unknown[]) => unknown;
  Choice: (normal: number, ...items: unknown[]) => unknown;
  Optional: (item: unknown, skip?: 'skip') => unknown;
  OneOrMore: (item: unknown, repeat?: unknown) => unknown;
  ZeroOrMore: (item: unknown, repeat?: unknown, skip?: 'skip') => unknown;
  Terminal: (text: string) => unknown;
  NonTerminal: (text: string) => unknown;
  Comment: (text: string) => unknown;
  Skip: () => unknown;
};

type RailroadRenderable = {
  format: (...padding: number[]) => RailroadRenderable;
  toSVG: () => SVGElement;
};

const Railroad = ((RailroadImport as { default?: RailroadLibrary }).default ??
  RailroadImport) as RailroadLibrary;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function expectItems(value: unknown, type: string) {
  if (!Array.isArray(value)) {
    throw new Error(`${type} requires an "items" array.`);
  }

  return value as RailroadSpec[];
}

function expectItem(value: unknown, type: string) {
  if (value === undefined) {
    throw new Error(`${type} requires an "item" value.`);
  }

  return value as RailroadSpec;
}

function expectText(value: unknown, type: string) {
  if (typeof value !== 'string' || value.length === 0) {
    throw new Error(`${type} requires a non-empty "text" string.`);
  }

  return value;
}

function createNode(spec: RailroadSpec): unknown {
  if (typeof spec === 'string') {
    return Railroad.Terminal(spec);
  }

  if (Array.isArray(spec)) {
    throw new Error(
      'Nested arrays are not supported inside railroad items. Wrap them in a Sequence or Diagram object.',
    );
  }

  if (!isRecord(spec) || typeof spec.type !== 'string') {
    throw new Error(
      'Each railroad node must be a string or an object with a "type" field.',
    );
  }

  switch (spec.type) {
    case 'Terminal':
      return Railroad.Terminal(expectText(spec.text, spec.type));
    case 'NonTerminal':
      return Railroad.NonTerminal(expectText(spec.text, spec.type));
    case 'Comment':
      return Railroad.Comment(expectText(spec.text, spec.type));
    case 'Skip':
      return Railroad.Skip();
    case 'Sequence':
      return Railroad.Sequence(
        ...expectItems(spec.items, spec.type).map(createNode),
      );
    case 'Choice':
      return Railroad.Choice(
        spec.normal ?? 0,
        ...expectItems(spec.items, spec.type).map(createNode),
      );
    case 'Optional':
      return Railroad.Optional(
        createNode(expectItem(spec.item, spec.type)),
        spec.skip,
      );
    case 'OneOrMore':
      return Railroad.OneOrMore(
        createNode(expectItem(spec.item, spec.type)),
        spec.repeat === undefined
          ? undefined
          : createNode(spec.repeat as RailroadSpec),
      );
    case 'ZeroOrMore':
      return Railroad.ZeroOrMore(
        createNode(expectItem(spec.item, spec.type)),
        spec.repeat === undefined
          ? undefined
          : createNode(spec.repeat as RailroadSpec),
        spec.skip,
      );
    case 'Diagram':
      return Railroad.Diagram(
        ...expectItems(spec.items, spec.type).map(createNode),
      );
    case 'ComplexDiagram':
      return Railroad.ComplexDiagram(
        ...expectItems(spec.items, spec.type).map(createNode),
      );
    default:
      // The union is exhaustive, so this is only reached by input that did not
      // come from it - which is exactly what the message should report.
      throw new Error(
        `Unsupported railroad node type: ${(spec as { type?: unknown }).type}`,
      );
  }
}

function createDiagram(spec: RailroadSpec): RailroadRenderable {
  if (Array.isArray(spec)) {
    return Railroad.Diagram(...spec.map(createNode));
  }

  if (typeof spec === 'string') {
    return Railroad.Diagram(createNode(spec));
  }

  if (!isRecord(spec)) {
    throw new Error('Railroad source must be a JSON object, array, or string.');
  }

  if (
    typeof spec.type === 'string' &&
    (spec.type === 'Diagram' || spec.type === 'ComplexDiagram')
  ) {
    return createNode(spec) as RailroadRenderable;
  }

  // A bare `{ items: [...] }` with no type is accepted as a diagram body. It
  // is not one of the declared variants, so it is read as the loose shape it
  // is rather than narrowed from the union.
  const loose = spec as { type?: unknown; items?: unknown };
  if (!('type' in spec) && Array.isArray(loose.items)) {
    return Railroad.Diagram(...loose.items.map(createNode));
  }

  return Railroad.Diagram(createNode(spec as RailroadSpec));
}

export function renderRailroadDiagrams(root: ParentNode) {
  const blocks = root.querySelectorAll<HTMLElement>(
    '[data-railroad-diagram="true"]',
  );

  for (const block of blocks) {
    if (block.dataset.railroadRendered === 'true') {
      continue;
    }

    const sourceNode = block.querySelector<HTMLScriptElement>(
      '.docs-railroad-source',
    );
    const renderNode = block.querySelector<HTMLElement>(
      '.docs-railroad-render',
    );

    if (!sourceNode || !renderNode) {
      continue;
    }

    try {
      const parsed = JSON.parse(
        sourceNode.textContent || 'null',
      ) as RailroadSpec;
      const diagram = createDiagram(parsed);
      diagram.format(16);

      const svg = diagram.toSVG();
      svg.setAttribute('role', 'img');
      svg.setAttribute('aria-label', 'Railroad diagram');

      renderNode.replaceChildren(svg);
      block.dataset.railroadRendered = 'true';
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      renderNode.replaceChildren();
      mount(RailroadError({ message }), renderNode);
      block.dataset.railroadRendered = 'error';
    }
  }
}
